<template>
  <div>
    <PageHeaderBanner>
      <template #title>在庫価格ファイル取込(CSV)</template>
    </PageHeaderBanner>
    <MessageChip :display="isMessageChip" level="E" :message="errMessage" />
    <div class="cmd00300-upload-form">
      <label class="text-weight-bold" style="font-size: 1.1rem">在庫価格ファイルアップロード</label>
      <div class="row q-gutter-x-xs items-center">
        <div class="col">
          <q-file
            v-model="uploadFiles"
            outlined
            multiple
            append
            dense
            clearable
            use-chips
            accept=".csv"
            @clear="uploadFiles = []"
          >
            <template #prepend>
              <q-icon name="attach_file" />
            </template>
          </q-file>
        </div>
        <div>
          <q-btn
            color="primary"
            label="アップロード"
            icon="mdi-upload"
            :disable="uploadFiles.length ? false : true"
            :class="uploadFiles.length ? '' : 'cmd00300-disabled-btn'"
            style="height: 40px"
            @click="displayUploadDialog"
          />
          <q-tooltip
            v-if="uploadFiles.length ? false : true"
            anchor="top middle"
            self="bottom middle"
            :offset="[15, 15]"
          >
            <strong>アップロードするファイルを選択してください</strong>
          </q-tooltip>
        </div>
      </div>
    </div>
    <div class="cmd00300-file-table q-mb-md">
      <label class="text-weight-bold" style="font-size: 1.1rem">
        アップロード在庫価格ファイル一覧
      </label>
      <div :class="'chem-table-area btn-exist ' + (fileData.length === 0 ? 'table-nodata' : '')">
        <q-table
          class="my-sticky-header-column-table"
          :rows="fileData"
          :columns="fileColumns"
          row-key="index"
          flat
          dense
          separator="cell"
          :hide-no-data="hideNoDataFlg"
          no-data-label="データがありません。"
          :rows-per-page-options="[10, 20, 50, 0]"
          rows-per-page-label="件表示"
          :pagination-label="
            (firstRowIndex, endRowIndex, totalRowsNumber) => {
              return `${totalRowsNumber} 件中 ${firstRowIndex}～${endRowIndex} 件表示`;
            }
          "
        >
          <template #no-data="{ message }">
            <div class="full-width row q-gutter-sm">
              <q-icon size="24px" name="mdi-alert-circle-outline" />
              <span class="q-pt-xs">{{ message }}</span>
            </div>
          </template>
          <!-- 編集削除Header -->
          <template #header-cell-editDelete>
            <q-th class="table-btn-area delete-btn-exists"></q-th>
          </template>
          <!-- 編集、削除Body -->
          <template #body-cell-editDelete="cell">
            <q-td v-if="!hideNoDataFlg" class="table-btn-area delete-btn-exists">
              <div class="flex">
                <q-btn
                  icon="mdi-pencil"
                  label="編集"
                  color="primary"
                  @click="editCsvFormat(cell.row.file_name)"
                />
                <q-btn
                  icon="mdi-close"
                  label="削除"
                  class="deletebtn"
                  outline
                  color="primary"
                  style="color: var(--q-delete)"
                  @click="displayDeleteUploadDialog(cell.row.file_name)"
                />
              </div>
            </q-td>
          </template>
          <template #body-cell="cell">
            <q-td v-if="!hideNoDataFlg" :props="cell">{{ cell.value }}</q-td>
          </template>
        </q-table>
      </div>
    </div>
    <div class="cmd00300-imported-table">
      <div class="row q-mb-sm">
        <div class="col">
          <label class="text-weight-bold" style="font-size: 1.1rem">
            取込済み在庫価格ファイル一覧
          </label>
        </div>
      </div>
      <div
        :class="
          'chem-table-area btn-exist table-top-button nofixed-column ' +
          (importedFileData.length === 0 ? 'table-nodata' : '')
        "
      >
        <q-table
          class="my-sticky-header-column-table"
          :rows="importedFileData"
          :columns="importedColumns"
          row-key="index"
          flat
          dense
          :visible-columns="visibleColumns"
          :options="visibleColumnOptions"
          separator="cell"
          :hide-no-data="hideNoDataFlg"
          no-data-label="データがありません。"
          :rows-per-page-options="[10, 20, 50, 0]"
          rows-per-page-label="件表示"
          :pagination-label="
            (firstRowIndex, endRowIndex, totalRowsNumber) => {
              return `${totalRowsNumber} 件中 ${firstRowIndex}～${endRowIndex} 件表示`;
            }
          "
        >
          <template #no-data="{ message }">
            <div class="full-width row q-gutter-sm">
              <q-icon size="24px" name="mdi-alert-circle-outline" />
              <span class="q-pt-xs">{{ message }}</span>
            </div>
          </template>
          <template #top>
            <!-- カラム表示設定 -->
            <div class="add-btn-area flex items-center filter-select justify-between">
              <q-select
                v-model="visibleColumns"
                multiple
                outlined
                dense
                options-dense
                display-value="カラム表示設定"
                emit-value
                map-options
                :options="visibleColumnOptions"
                option-value="name"
                color="primary"
                class="column-disp"
              />
              <q-btn
                color="primary"
                outline
                label="更新"
                style="width: 100px"
                icon="mdi-autorenew"
                @click="reloadLog"
              />
            </div>
          </template>

          <!-- 中止ボタンHeader -->
          <template #header-cell-stop="val">
            <q-th class="table-btn-area stopbtn-area">{{ val.col.label }}</q-th>
          </template>

          <!-- ダウンロードHeader -->
          <template #header-cell-download="val">
            <q-th class="table-btn-area right-table-btn">{{ val.col.label }}</q-th>
          </template>

          <!-- 中止ボタンBody -->
          <template #body-cell-stop="cell">
            <q-td class="table-btn-area stopbtn-area">
              <div class="flex">
                <q-btn
                  v-if="cell.row.status === FileStatus.default.BUSY"
                  style="
                    width: 50px;
                    background-color: var(--q-brightRed);
                    color: var(--q-pureWhite);
                    letter-spacing: 4px;
                    padding-left: 14px;
                  "
                  label="中止"
                  @click="
                    displayStopImportDialog(
                      cell.row.stock_price_file_name,
                      cell.row.stock_price_file_id
                    )
                  "
                />
                <label v-else style="height: 36px"></label>
              </div>
            </q-td>
          </template>

          <!-- ダウンロードBody -->
          <template #body-cell-download="cell">
            <q-td>
              <a
                style="text-decoration: none; color: red"
                :href="`/cmd003001/download-err-csv?fileId=${cell.row.stock_price_file_id}`"
              >
                <q-chip
                  v-if="cell.row.err_file_name"
                  clickable
                  outline
                  square
                  color="red"
                  text-color="black"
                  icon="mdi-download"
                >
                  {{ cell.row.err_file_name }}
                </q-chip>
              </a>
            </q-td>
          </template>
          <template #body-cell-status="props">
            <q-td :props="props" :style="isErrStatusExist(props.row.status) ? 'color: red' : ''">
              <!-- 完了(エラー有り) -->
              <q-icon
                v-if="isCompErrExist(props.row.status, props.row.err_file_name)"
                size="20px"
                name="mdi-alert"
                style="color: var(--q-informationPublish)"
              />
              <!-- エラー系 -->
              <q-icon
                v-else-if="isErrStatusExist(props.row.status)"
                size="20px"
                name="mdi-close-circle"
              />
              <!-- 中止 -->
              <q-icon
                v-else-if="props.row.status === FileStatus.default.ABORT"
                size="20px"
                name="mdi-cancel"
              />
              <!-- 完了 -->
              <q-icon
                v-else-if="props.row.status === FileStatus.default.COMPLETED"
                size="20px"
                name="mdi-check-circle"
                style="color: green"
              />
              <!-- チェック中 -->
              <q-icon
                v-else-if="props.row.status === FileStatus.default.CSV_BEING_IMPORTED"
                size="20px"
                name="mdi-progress-check"
              />
              <!-- スケジュール済み -->
              <q-icon v-else size="20px" name="mdi-calendar-check" />
              {{
                isCompErrExist(props.row.status, props.row.err_file_name)
                  ? '完了(エラー有り)'
                  : FileStatus.getName(props.row.status)
              }}
            </q-td>
          </template>
          <template #body-cell-import_num="props">
            <q-td :props="props">
              {{ props.row.import_num ? commaFormat(props.row.import_num) : props.row.import_num }}
            </q-td>
          </template>
        </q-table>
      </div>
    </div>
    <!-- アップロードダイアログ -->
    <AlertDialog
      :display="isUploadDisplay"
      :level="dialogLevel"
      :confirmation="isConfFlg"
      :title="dialogTitle"
      :text="dialogText"
      @complete="isUploadDisplay = false"
      @confirm="checkUploadFile"
      @close="isUploadDisplay = false"
    />
    <!-- アップロード削除ダイアログ -->
    <AlertDialog
      :display="isUploadDeleteDisplayFlg"
      :level="dialogLevel"
      :confirmation="isConfFlg"
      :title="dialogTitle"
      :text="dialogText"
      @complete="isUploadDeleteDisplayFlg = false"
      @confirm="deleteUploadFile"
      @close="isUploadDeleteDisplayFlg = false"
    />
    <!-- 中止ダイアログ -->
    <AlertDialog
      :display="isStopImportDisplay"
      :level="dialogLevel"
      :confirmation="isConfFlg"
      :title="dialogTitle"
      :text="dialogText"
      @complete="isStopImportDisplay = false"
      @confirm="stopImport"
      @close="isStopImportDisplay = false"
    />
  </div>
</template>
<script setup lang="ts">
import { ref } from 'vue';
import { Loading } from 'quasar';
import { useRouter } from 'vue-router';
import PageHeaderBanner from '../../components/banner/PageHeaderBanner.vue';
import axios from '../../common/axios-common';
import DetailResultStatusCode from '../../common/code/DetailResultStatusCode';
import HttpResponseStatusCode from '../../common/code/HttpResponseStatusCode';
import ResultStatusCode from '../../common/code/ResultStatusCode';
import commaFormat from '../../common/utils/numberUtils';
import * as FileStatus from '../../common/code/StockPriceStatusCode';
import MessageChip from '../../components/chip/MessageChip.vue';
import AlertDialog from '../../components/dialogs/AlertDialog.vue';
import { isFilesExist } from '../../common/utils/fileUtils';

// アップロードCSV一覧
interface UploadCsvList {
  file_name: string;
  file_size: string;
  update_date: string;
}

// 取込済み在庫価格ファイル一覧
interface ImportedFileList {
  stock_price_file_id: number;
  stock_price_file_name: string;
  supplier_name: string;
  sup_category: string;
  status: string;
  exec_date_str: string;
  import_start_time_str: string;
  import_end_time_str: string;
  import_num: string;
  memo: string;
  err_file_name: string;
}

// カラム表示機能
interface VisibleColumn {
  name: string;
  label: string;
}

const router = useRouter();

// 表示カラム
const visibleColumns = ref<Array<string>>([]);
// カラム表示設定の選択肢
const visibleColumnOptions = ref<Array<VisibleColumn>>([]);

// エラーメッセージ
const errMessage = ref('');
// MessageChip表示フラグ
const isMessageChip = ref(false);
// 初期表示読み込み時「データがありません」を非表示にするためのフラグ(falseで表示)
const hideNoDataFlg = ref(true);

// Dialogタイトル
const dialogTitle = ref('');
// Dialog表示レベル
const dialogLevel = ref('');
// Dialog本文
const dialogText = ref(['']);
// 確認Dialogフラグ
const isConfFlg = ref(false);

// アップロードDialog表示フラグ
const isUploadDisplay = ref(false);
// アップロード削除Dialog表示フラグ
const isUploadDeleteDisplayFlg = ref(false);
// 中止Dialog表示フラグ
const isStopImportDisplay = ref(false);

// 在庫・価格ファイルID
const stockPriceFileId = ref('');
// CSVファイル名
const csvFileName = ref('');
// アップロードファイル
const uploadFiles = ref<File[]>([]);
// アップロードファイルチェックフラグ
const uploadCheck = ref(0);

// アップロードSDFile一覧カラム
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const fileColumns: Array<any> = [
  {
    name: 'editDelete',
    field: (row: UploadCsvList) => row.file_name,
    required: true,
  },
  {
    name: ' file_name',
    label: '在庫価格ファイル名',
    align: 'left',
    field: (row: UploadCsvList) => row.file_name,
    sortable: true,
  },
  {
    name: 'file_size',
    label: 'ファイルサイズ',
    align: 'left',
    field: (row: UploadCsvList) => row.file_size,
    classes: 'text-right',
    style: 'width: 160px',
    sortable: true,
  },
  {
    name: 'update_date',
    label: '更新日時',
    align: 'left',
    field: (row: UploadCsvList) => row.update_date,
    style: 'width: 160px',
    sortable: true,
  },
];

// 取込済み在庫価格ファイル一覧カラム
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const importedColumns: Array<any> = [
  {
    name: 'stop',
    align: 'center',
    field: 'stop',
    style: 'width: 140px',
    required: true,
  },
  {
    name: 'stock_price_file_name',
    label: '在庫価格ファイル名',
    align: 'left',
    field: (row: ImportedFileList) => row.stock_price_file_name,
    sortable: true,
  },
  {
    name: 'supplier_name',
    label: 'サプライヤ名',
    align: 'left',
    field: (row: ImportedFileList) => row.supplier_name,
    sortable: true,
  },
  {
    name: 'sup_category',
    label: 'カテゴリー',
    align: 'left',
    field: (row: ImportedFileList) => row.sup_category,
    sortable: true,
  },
  {
    name: 'status',
    label: 'ステータス',
    align: 'left',
    field: (row: ImportedFileList) => row.status,
    sortable: true,
  },
  {
    name: 'exec_date_str',
    label: '取込予定日時',
    align: 'left',
    field: (row: ImportedFileList) => row.exec_date_str,
    style: 'width: 140px',
    sortable: true,
  },
  {
    name: 'import_start_time_str',
    label: '取込開始日時',
    align: 'left',
    field: (row: ImportedFileList) => row.import_start_time_str,
    style: 'width: 140px',
    sortable: true,
  },
  {
    name: 'import_end_time_str',
    label: '取込完了日時',
    align: 'left',
    field: (row: ImportedFileList) => row.import_end_time_str,
    style: 'width: 140px',
    sortable: true,
  },
  {
    name: 'import_num',
    label: '取込品目数',
    align: 'left',
    field: (row: ImportedFileList) => row.import_num,
    classes: 'text-right',
    sortable: true,
  },
  {
    name: 'memo',
    label: '備考',
    align: 'left',
    field: (row: ImportedFileList) => row.memo,
    sortable: true,
  },
  {
    name: 'download',
    label: 'エラーファイル',
    field: (row: ImportedFileList) => row.err_file_name,
  },
];

// アップロードSDFile一覧データ
const fileData = ref<UploadCsvList[]>([]);

// 取込済み在庫価格ファイル一覧データ
const importedFileData = ref<ImportedFileList[]>([]);

/**
 * 完了ステータスでエラーファイルが存在するか判定
 * @param {string} status ファイルステータス
 * @param {string} errFile エラーファイル
 * @returns 条件に該当する場合true
 */
const isCompErrExist = (status: string, errFile: string) => {
  // Statusが完了かつエラーファイルが存在する場合
  if (status === FileStatus.default.COMPLETED && errFile) {
    return true;
  }
  return false;
};

/**
 * エラーのステータスであることを判定
 * @param {string} status ファイルステータス
 * @param {string} errFile エラーファイル
 * @returns 条件に該当する場合true
 */
const isErrStatusExist = (status: string) => {
  if (
    // ファイルステータスがシステムエラーまたはファイルフォーマットエラーの場合
    status === FileStatus.default.SYSTEM_ERROR ||
    status === FileStatus.default.FILE_FORMAT_ERROR
  ) {
    return true;
  }
  return false;
};

/**
 * アップロード確認ダイアログ表示
 */
const displayUploadDialog = () => {
  dialogTitle.value = 'アップロード確認';
  dialogLevel.value = 'information';
  dialogText.value = ['選択したファイルをアップロードします。', 'よろしいですか？'];
  isConfFlg.value = false;
  isUploadDisplay.value = true;
};

/**
 * アップロード完了ダイアログ
 */
const completeUploadDialog = () => {
  dialogTitle.value = 'アップロード完了';
  dialogLevel.value = 'information';
  dialogText.value = ['ファイルのアップロードが完了しました。'];
  isConfFlg.value = true;
  isUploadDisplay.value = true;
};

/**
 * 取込中止確認ダイアログ表示
 * @param {string} fileName CSVファイル名
 * @param {string} fileId 在庫・価格ファイルID
 */
const displayStopImportDialog = (fileName: string, fileId: string) => {
  stockPriceFileId.value = fileId;
  dialogTitle.value = '取込中止確認';
  dialogLevel.value = 'information';
  dialogText.value = [`${fileName}の取込処理を中止します。`, 'よろしいですか？'];
  isConfFlg.value = false;
  isStopImportDisplay.value = true;
};

/**
 * 取込中止受付完了ダイアログ
 */
const completeStopImportDialog = () => {
  dialogTitle.value = '受付完了';
  dialogLevel.value = 'information';
  dialogText.value = ['取込中止要求を受け付けました。'];
  isConfFlg.value = true;
  isStopImportDisplay.value = true;
};

/**
 * 取込中止失敗ダイアログ
 */
const failStopImportDialog = () => {
  dialogTitle.value = '取込中止エラー';
  dialogLevel.value = 'error';
  dialogText.value = [
    'ステータスが変更されたため、取込処理を中止できませんでした。',
    'データを再度確認してください。',
  ];
  isConfFlg.value = true;
  isStopImportDisplay.value = true;
};

/**
 * アップロード削除確認ダイアログ表示
 * @param {string} fileName ファイル名
 */
const displayDeleteUploadDialog = (fileName: string) => {
  csvFileName.value = fileName;
  dialogTitle.value = '削除確認';
  dialogLevel.value = 'information';
  dialogText.value = [`${fileName}を削除します。`, 'よろしいですか？'];
  isConfFlg.value = false;
  isUploadDeleteDisplayFlg.value = true;
};

/**
 * 削除完了ダイアログ
 */
const completeDeleteDialog = () => {
  dialogTitle.value = '削除完了';
  dialogLevel.value = 'information';
  dialogText.value = ['削除が完了しました。'];
  isConfFlg.value = true;
};

/**
 * 初期表示データ取得
 */
const getInitData = () => {
  // Loading Start
  Loading.show();

  axios
    .post('/cmd003001/init')
    .then((res) => {
      const { common, uploadCsvList, importedCsvList } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          fileData.value = uploadCsvList;
          importedFileData.value = importedCsvList;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          router.push({ name: 'error' });
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
      hideNoDataFlg.value = false;
    });
};

/**
 * ファイルアップロード
 */
const uploadFile = async () => {
  isUploadDisplay.value = false;
  // エラー初期化
  errMessage.value = '';
  isMessageChip.value = false;

  // Loading Start
  Loading.show();

  const formData = new FormData();
  Object.entries(uploadFiles.value).forEach(([, value]) => {
    formData.append('fileList', value);
  });

  axios
    .post('/cmd003001/csv-upload', formData)
    .then((res) => {
      const { common, uploadCsvList } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          fileData.value = uploadCsvList;
          uploadFiles.value = [];
          completeUploadDialog();
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            case DetailResultStatusCode.FILE_NOT_EXIST_ERROR:
              errMessage.value = common.message;
              isMessageChip.value = true;
              break;

            // システムエラーの場合
            case DetailResultStatusCode.PARAMETER_ERROR:
            case DetailResultStatusCode.SYSTEM_ERROR:
              router.push({ name: 'error' });
              break;

            default:
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * ファイルアップロードチェック
 */
const checkUploadFile = async () => {
  isFilesExist(uploadFiles.value).then((value) => {
    if (value) {
      uploadFile();
    } else {
      uploadCheck.value = 0;
      Promise.all(
        Object.entries(uploadFiles.value).map(async (item) => {
          if (item[1].size === 0) {
            uploadCheck.value += 1;
          }
        })
      );
      dialogTitle.value = 'ファイル読み込みエラー';
      dialogLevel.value = 'error';
      if (uploadCheck.value !== 0) {
        dialogText.value = [
          '選択したファイルは内容が設定されていません。',
          'ファイルを再度選択してください。',
        ];
      } else {
        dialogText.value = [
          '更新または削除されたファイルがあるため、アップロードできませんでした。',
          'ファイルを再度選択してください。',
        ];
      }
      isConfFlg.value = true;
      isUploadDisplay.value = true;
    }
  });
};

/**
 * アップロードファイル削除
 */
const deleteUploadFile = () => {
  isUploadDeleteDisplayFlg.value = false;
  // Loading Start
  Loading.show();
  const formData = new FormData();
  formData.append('file_name', csvFileName.value);

  axios
    .post('/cmd003001/delete-upload-file', formData)
    .then((res) => {
      const { common, uploadCsvList } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          fileData.value = uploadCsvList;
          completeDeleteDialog();
          isUploadDeleteDisplayFlg.value = true;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            // ファイルが存在しない場合
            case DetailResultStatusCode.FILE_NOT_EXIST_ERROR:
              fileData.value = uploadCsvList;
              dialogTitle.value = 'ファイル削除エラー';
              dialogLevel.value = 'error';
              dialogText.value = [common.message];
              isConfFlg.value = true;
              isUploadDeleteDisplayFlg.value = true;
              break;

            // パラメータエラーの場合
            case DetailResultStatusCode.PARAMETER_ERROR:
            case DetailResultStatusCode.SYSTEM_ERROR:
              router.push({ name: 'error' });
              break;

            default:
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * 取込中止
 */
const stopImport = () => {
  // Loading Start
  Loading.show();
  const formData = new FormData();
  formData.append('stock_price_file_id', stockPriceFileId.value);

  axios
    .post('/cmd003001/stop-import', formData)
    .then((res) => {
      const { common, importedCsvList } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          importedFileData.value = importedCsvList;
          // ダイアログ表示
          completeStopImportDialog();
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            // ファイルステータスがすでに中止されている場合
            case DetailResultStatusCode.SELECT_DATA_MISMATCH:
              importedFileData.value = importedCsvList;
              // ダイアログ表示
              failStopImportDialog();
              break;

            // パラメータエラーの場合
            case DetailResultStatusCode.PARAMETER_ERROR:
            case DetailResultStatusCode.SYSTEM_ERROR:
              router.push({ name: 'error' });
              break;

            default:
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * ログ再読み込み
 */
const reloadLog = () => {
  // Loading Start
  Loading.show();

  axios
    .post('/cmd003001/reload-log')
    .then((res) => {
      const { common, importedCsvList } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          importedFileData.value = importedCsvList;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          router.push({ name: 'error' });
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * 在庫価格ファイル取込フォーマット指定画面に遷移
 * @param {string} fileName ファイル名
 */
const editCsvFormat = (fileName: string) => {
  router.push({ name: 'stockPriceSetFormat', params: { fileName } });
};

/**
 * 初期表示処理
 */
getInitData();
// 取込済み在庫価格ファイル一覧のカラム表示設定
for (let i = 0; i < importedColumns.length; i += 1) {
  visibleColumns.value.push(importedColumns[i].name);
  if (importedColumns[i].name !== 'stop') {
    visibleColumnOptions.value.push({
      name: importedColumns[i].name,
      label: importedColumns[i].label,
    });
  }
}
</script>
<style scoped>
.cmd00300-upload-form {
  margin: 0 0 40px 5%;
  width: 90%;
  max-width: unset;
  padding: 0;
}

.cmd00300-file-table,
.cmd00300-imported-table {
  width: 90%;
  margin: 0 auto 20px;
}

.cmd00300-imported-table {
  margin-bottom: 0;
}

.chem-table-area.btn-exist .table-btn-area.stopbtn-area {
  width: 60px;
  min-width: 60px;
}

.chem-table-area.btn-exist th.table-btn-area.right-table-btn {
  min-width: 156px;
}
</style>
<style>
.cmd00300-file-table .btn-exist.chem-table-area .q-table__container {
  width: 100%;
  margin: 3px auto 0;
}

.cmd00300-imported-table .chem-table-area .q-table__bottom.row {
  right: 100px;
}

.cmd00300-imported-table .q-table__top {
  width: 100%;
}

.cmd00300-upload-form .cmd00300-disabled-btn {
  background-color: var(--q-opacityDisable) !important;
}

.cmd00300-upload-form .q-field .q-field__control.row {
  align-items: center;
}
</style>
